import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { SsoRuleToCreate, SsoRuleToUpdate } from '@robocorp/robocloud-sso-management-interface';
import {
  getOrganizations,
  getRules,
  createRule,
  deleteRule,
  editRule,
  createSsoWorkspace,
} from '~/services/userAPI/ssoManagementService';
import { useIsSSO } from '~/hooks/useIsSSO';
import { validateApiResponse, formatErrorPayload } from '../utils';

export enum SsoManagementQueryErrors {
  RBAC_GET_RULES_ERROR = 'RBAC_GET_RULES_ERROR ',
  RBAC_GET_ORGANIZATIONS_ERROR = 'RBAC_GET_ORGANIZATIONS_ERROR',
  RBAC_CREATE_RULE_ERROR = 'RBAC_CREATE_RULE_ERROR',
  RBAC_EDIT_RULE_ERROR = 'RBAC_EDIT_RULE_ERROR',
  RBAC_DELETE_RULE_ERROR = 'RBAC_DELETE_RULE_ERROR',
  RBAC_CREATE_WORKSPACE_ERROR = 'RBAC_CREATE_WORKSPACE_ERROR',
}

const getSsoRulesQueryKey = ({ realm }: { realm: string }) => [
  'sso-management.getRules',
  { realm },
];
const getSsoOrganizationsQueryKey = ({ realm }: { realm: string }) => [
  'sso-management.getOrganizations',
  { realm },
];

export const useSsoManagementMutations = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { realm } = useIsSSO();

  if (realm === undefined) {
    throw Error('Unexpected error');
  }

  return {
    createRule: useMutation(
      async (ssoRule: SsoRuleToCreate) => {
        const response = validateApiResponse(await createRule({ realm }, ssoRule));
        return response.data;
      },
      {
        onSuccess: (data) => {
          if (!data) return;

          queryClient.invalidateQueries(getSsoRulesQueryKey({ realm }));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: SsoManagementQueryErrors.RBAC_CREATE_RULE_ERROR,
            }),
          );
        },
      },
    ),
    editRule: useMutation(
      async (ssoRule: SsoRuleToUpdate) => {
        const response = validateApiResponse(await editRule({ realm }, ssoRule));
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(getSsoRulesQueryKey({ realm }));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: SsoManagementQueryErrors.RBAC_EDIT_RULE_ERROR,
            }),
          );
        },
      },
    ),
    deleteRule: useMutation(
      async (ruleId: string) => {
        const response = validateApiResponse(await deleteRule({ realm, ruleId }));
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(getSsoRulesQueryKey({ realm }));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: SsoManagementQueryErrors.RBAC_DELETE_RULE_ERROR,
            }),
          );
        },
      },
    ),
    createWorkspace: useMutation(
      async (workspaceToCreate: {
        workspaceName: string;
        organizationId: string;
        isAgentWorkspace: boolean;
      }) => {
        const response = validateApiResponse(
          await createSsoWorkspace({
            realm,
            organizationId: workspaceToCreate.organizationId,
            workspaceName: workspaceToCreate.workspaceName,
            isAgentWorkspace: workspaceToCreate.isAgentWorkspace,
          }),
        );
        return response.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(getSsoOrganizationsQueryKey({ realm }));
        },
        onError: (error: Error) => {
          dispatch(
            formatErrorPayload(error, {
              type: SsoManagementQueryErrors.RBAC_CREATE_WORKSPACE_ERROR,
            }),
          );
        },
      },
    ),
  };
};

export const useSsoManagementGetRulesQuery = () => {
  const dispatch = useDispatch();

  const { realm } = useIsSSO();

  if (realm === undefined) {
    throw Error('Unexpected error');
  }

  return useQuery(
    getSsoRulesQueryKey({ realm }),
    async () => validateApiResponse(await getRules({ realm })).data,
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: SsoManagementQueryErrors.RBAC_GET_RULES_ERROR,
          }),
        );
      },
      placeholderData: [],
    },
  );
};

export const useSsoManagementGetOrganizationsQuery = () => {
  const dispatch = useDispatch();

  const { realm } = useIsSSO();

  if (realm === undefined) {
    throw Error('Unexpected error');
  }

  return useQuery(
    getSsoOrganizationsQueryKey({ realm }),
    async () => validateApiResponse(await getOrganizations({ realm })).data,
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: SsoManagementQueryErrors.RBAC_GET_ORGANIZATIONS_ERROR,
          }),
        );
      },
      placeholderData: [],
    },
  );
};
